import { useEffect, useState, useMemo } from 'react';
import { apiWithEntityId } from 'services/api';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { StatusMapper } from 'types/settings/internalConfig';

interface ProcessorStatusMappers {
  [processorId: string]: StatusMapper | null;
}

interface ProcessorConfigurationData {
  id: string;
  version: number;
  configurations: unknown[];
  defaultConfigurations: unknown[];
  deviceConfigurationMode: string;
  defaultChannelConfig: unknown[];
  statusMapper?: StatusMapper | null;
}

interface ProcessorConfigurationResponse {
  message: string;
  data: ProcessorConfigurationData;
  errorRaw: unknown;
}

export const useProcessorStatusMappers = (
  processors: PAYMENT_CONFIG.IProcessors[]
) => {
  const [processorStatusMappers, setProcessorStatusMappers] =
    useState<ProcessorStatusMappers>({});

  const processorIds = useMemo(() => {
    return processors.map((p) => p.processorId).sort().join(',');
  }, [processors]);

  useEffect(() => {
    const fetchStatusMappers = async () => {
      if (processors.length === 0) {
        return;
      }

      const results = await Promise.all(
        processors.map(async (processor) => {
          try {
            const response = await apiWithEntityId.get<ProcessorConfigurationResponse>(
              `/processors/${processor.processorName}/configuration`
            );
            return {
              processorId: processor.processorId,
              statusMapper: response?.data?.data?.statusMapper ?? null,
            };
          } catch (error) {
            console.error(
              `Failed to fetch status mapper for processor ${processor.processorName}:`,
              error
            );
            return { processorId: processor.processorId, statusMapper: null };
          }
        })
      );

      const mappers: ProcessorStatusMappers = {};
      for (const { processorId, statusMapper } of results) {
        mappers[processorId] = statusMapper;
      }

      setProcessorStatusMappers(mappers);
    };

    fetchStatusMappers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processorIds]);

  return processorStatusMappers;
};
